import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircle2,
  Download,
  Calendar,
  ArrowRight,
  Sparkles,
  User,
  Ticket,
} from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";
import ConferenceCard from "../../components/event_media/ConferenceCard";

const BASE_PATH = "/event-media-sales";

export default function AgentConferenceRegistrationSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const stateData = location.state || {};

  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const applicationId = stateData.applicationId ?? stateData.registrationData?.application_id ?? stateData.registrationData?.id ?? null;
  const eventData = stateData.eventData ?? null;
  const payLater = stateData.payLater === true;
  const payNow = stateData.payNow === true;

  useEffect(() => {
    if (!slug) {
      setLoadingEvents(false);
      return;
    }
    const fetchEvent = async () => {
      try {
        const response = await conferenceAndTradeMissionService.getEventDetails(slug);
        const similar = response.similar_event ?? response.data?.similar_event ?? [];
        setFeaturedEvents(Array.isArray(similar) ? similar : []);
      } catch (err) {
        console.error("Error fetching similar events:", err);
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvent();
  }, [slug]);

  const handleDownloadReceipt = () => {
    toast.info("Receipt download will be available soon.");
  };

  const getMessage = () => {
    if (payLater) {
      return "Registration complete. Your client can pay later. We've received the registration and will send a confirmation with payment options.";
    }
    if (payNow) {
      return "Registration submitted. Payment can be completed from the event details page or via the link we send to your client.";
    }
    return "We've received the conference registration for your client. A confirmation will be sent shortly with all the details.";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="px-4 py-8 pt-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Main content */}
            <div className="lg:col-span-2">
              <motion.button
                onClick={() => navigate(BASE_PATH)}
                whileHover={{ x: -4 }}
                whileTap={{ scale: 0.98 }}
                className="flex gap-2 items-center px-4 py-2 mb-8 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
              >
                <ChevronLeft size={18} />
                Back to Event & Media Sales
              </motion.button>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <h1 className="mb-2 text-3xl font-bold text-gray-900 sm:text-4xl">
                  Registration successful
                </h1>

                {applicationId && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mb-4 text-sm font-medium text-gray-600"
                  >
                    Application ID: {applicationId}
                  </motion.p>
                )}

                {eventData?.event_title && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.25 }}
                    className="mb-8 text-lg font-semibold text-orange-600"
                  >
                    {eventData.event_title}
                  </motion.p>
                )}

                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  className="flex justify-center mb-6"
                >
                  <div className="relative">
                    <div className="flex justify-center items-center w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full border-4 border-orange-300 shadow-xl">
                      <CheckCircle2 size={64} className="text-white drop-shadow-lg" />
                    </div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ delay: 0.5, duration: 2, repeat: Infinity }}
                      className="absolute inset-0 bg-orange-400 rounded-full opacity-20 animate-pulse"
                    />
                  </div>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mb-2 text-xl font-bold text-gray-900"
                >
                  Your client's registration has been confirmed
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mb-8 text-base text-gray-600 max-w-xl mx-auto"
                >
                  {getMessage()}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex flex-col gap-3 justify-center items-center sm:flex-row"
                >
                  <motion.button
                    type="button"
                    onClick={handleDownloadReceipt}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-orange-700 rounded-lg border-2 border-orange-300 bg-orange-50/80 hover:bg-orange-100 hover:border-orange-400 transition-all"
                  >
                    <Download size={18} />
                    Download receipt
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => slug && navigate(`${BASE_PATH}/event-details/${slug}`)}
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-xl hover:from-orange-600 hover:to-orange-700 transition-all"
                  >
                    <Calendar size={18} />
                    View event
                    <ArrowRight size={16} />
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => navigate(`${BASE_PATH}/event-details/${slug}/register`)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-gray-700 bg-white rounded-lg border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all"
                  >
                    <User size={18} />
                    Register another client
                  </motion.button>
                </motion.div>
              </motion.div>

              {/* What's next */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="mt-12 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200"
              >
                <div className="flex gap-3 items-start">
                  <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-emerald-500 shrink-0">
                    <Sparkles size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">What happens next?</h3>
                    <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                      <li>Your client will receive a confirmation email with event details.</li>
                      {payLater && <li>Payment instructions will be sent so they can complete payment when ready.</li>}
                      <li>You can view or share the event page anytime from Event & Media Sales.</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar - Similar events */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col items-center lg:items-start"
              >
                <div className="flex flex-col items-center mb-6 w-full lg:items-start">
                  <h2 className="mb-2 text-xl font-bold text-gray-900">Similar events</h2>
                  <motion.button
                    type="button"
                    onClick={() => navigate(`${BASE_PATH}/conference`)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex gap-1 items-center text-sm font-semibold text-orange-600 hover:text-orange-700"
                  >
                    See all
                    <ArrowRight size={14} />
                  </motion.button>
                </div>

                {loadingEvents ? (
                  <div className="flex justify-center items-center py-8 w-full">
                    <div className="w-8 h-8 rounded-full border-2 border-orange-500 animate-spin border-t-transparent" />
                  </div>
                ) : featuredEvents.length === 0 ? (
                  <div className="py-8 w-full text-center">
                    <Ticket size={40} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-600">No similar events right now.</p>
                  </div>
                ) : (
                  <div className="space-y-3 w-full">
                    {featuredEvents.slice(0, 4).map((event, index) => (
                      <motion.div
                        key={event.slug || index}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + index * 0.08 }}
                      >
                        <ConferenceCard
                          item={event}
                          onClick={() => navigate(`${BASE_PATH}/event-details/${event.slug}`)}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <AuthenticatedFooter />
    </div>
  );
}
